import { Box, Typography, useTheme } from "@mui/material";
import { TaskerMessage, TaskerConfig } from "@t3/shared";
import { getStatusIcon, formatStatusText } from "./utils/statusHelpers";
import { RewardOptionDisplay } from "./RewardOptionDisplay";
import { ProgramCard } from "./ProgramCard";

interface StatusOnlySectionProps {
  reviewStatus?: string;
  rewardOptions?: TaskerMessage["rewardOptions"];
  userTaskerConfigs: TaskerConfig[];
  eligibleProgramIds: string[];
}

export const StatusOnlySection = ({
  reviewStatus,
  rewardOptions,
  userTaskerConfigs,
  eligibleProgramIds,
}: StatusOnlySectionProps) => {
  const theme = useTheme();

  // Get eligible configs and match reward options to programs
  const eligibleConfigs = userTaskerConfigs.filter((config) =>
    eligibleProgramIds.includes(config.programId),
  );

  return (
    <Box
      data-cypress="status-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          m: 1,
        }}
      >
        {getStatusIcon(reviewStatus, theme)}
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {formatStatusText(reviewStatus)}
        </Typography>
      </Box>
      {reviewStatus === "approved" && rewardOptions && (
        <Box sx={{ mt: 0 }}>
          {eligibleConfigs.map((config) => {
            // Find reward options that belong to this program
            const programRewardOptions = rewardOptions.filter((option) =>
              config.rewardOptions.some((ro) => ro.id === option.id),
            );

            // Skip if no reward options for this program
            if (!programRewardOptions.length) return null;
            return (
              <ProgramCard
                key={`${config.programId}-${config.id}`}
                programName={config.programName}
                programColor={config.programColor}
              >
                {programRewardOptions.map((option) => (
                  <RewardOptionDisplay
                    key={option.id}
                    option={option}
                    programColor={config.programColor}
                  />
                ))}
              </ProgramCard>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
